import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../../supabaseClient';
import { normalizePhone, formatPhone } from '../../../utils/phone';
import { normalizeEmail } from '../../../utils/email';
import { logAudit, diffFields } from '../../../utils/audit';
import RecordHistory from '../Audit/RecordHistory';

const CONTACT_FIELDS = ['first_name', 'last_name', 'email', 'phone'];

export default function ContactEditForm({ contact, onClose, onSave }) {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const firstNameRef = useRef(null);

  useEffect(() => {
    if (contact) {
      setFormData({
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        email: contact.email || '',
        phone: formatPhone(contact.phone),
      });
    } else {
      // Creating a new contact: put the cursor in the first name field.
      firstNameRef.current?.focus();
    }
  }, [contact]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const newValues = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: normalizeEmail(formData.email) || null,
        phone: normalizePhone(formData.phone),
      };

      if (contact) {
        // Update existing contact
        const { error: updateError } = await supabase
          .from('contacts')
          .update({ ...newValues, updated_at: new Date().toISOString() })
          .eq('id', contact.contact_id);

        if (updateError) throw updateError;

        // Normalize the old phone so a format-only edit doesn't register a change.
        const changes = diffFields(
          { ...contact, phone: normalizePhone(contact.phone) },
          newValues,
          CONTACT_FIELDS
        );
        if (changes) {
          await logAudit({
            action: 'contact.updated',
            entityType: 'contact',
            entityId: contact.contact_id,
            entityLabel: `${newValues.first_name} ${newValues.last_name}`,
            changes,
          });
        }
      } else {
        // Create new contact
        const { data: inserted, error: insertError } = await supabase
          .from('contacts')
          .insert(newValues)
          .select('id')
          .single();

        if (insertError) throw insertError;

        await logAudit({
          action: 'contact.created',
          entityType: 'contact',
          entityId: inserted?.id,
          entityLabel: `${newValues.first_name} ${newValues.last_name}`,
          changes: newValues,
        });
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('Error saving contact:', err);
      setError(err.message || 'Failed to save contact');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    {/* Portal: keeps `fixed inset-0` clear of the caller's space-y-* sibling margin. */}
    {createPortal(
    <div className="admin-content fixed inset-0 bg-gray-500 bg-opacity-75 flex items-start sm:items-center justify-center p-4 overflow-y-auto z-50">
      <div className="bg-white dark:bg-night-800 rounded-lg shadow-xl max-w-lg w-full modal-panel overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-night-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {contact ? 'Edit Contact' : 'New Contact'}
          </h2>
          {contact && (
            <button
              type="button"
              onClick={() => setShowHistory(true)}
              className="text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-900"
            >
              History
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4">
          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="first_name"
                  ref={firstNameRef}
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-night-600 shadow-sm dark:bg-night-700 dark:text-gray-100 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="last_name"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 dark:border-night-600 shadow-sm dark:bg-night-700 dark:text-gray-100 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Email
              </label>
              <input
                type="email"
                spellCheck={false}
                id="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-night-600 shadow-sm dark:bg-night-700 dark:text-gray-100 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Phone
              </label>
              <input
                type="tel"
                spellCheck={false}
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(123) 456-7890"
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-night-600 shadow-sm dark:bg-night-700 dark:text-gray-100 dark:placeholder-gray-400 focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-night-800 border border-gray-300 dark:border-night-600 rounded-md shadow-sm hover:bg-gray-50 dark:bg-night-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {loading ? 'Saving...' : (contact ? 'Save Changes' : 'Create Contact')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
    )}
    {showHistory && contact && (
      <RecordHistory
        entityType="contact"
        entityId={contact.contact_id}
        entityLabel={`${formData.first_name} ${formData.last_name}`}
        onClose={() => setShowHistory(false)}
      />
    )}
    </>
  );
}
